import { useState, FormEvent } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export default function AttendPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [currentStep, setCurrentStep] = useState<'invitation' | 'form' | 'review' | 'accepted' | 'confirmed'>('invitation');
  const [formData, setFormData] = useState({
    fullName: '',
    title: '',
    organization: '',
    primaryGeographies: '',
    primaryInterest: [] as string[],
    topGoals: '',
    applyFor: [] as string[],
    dietaryNotes: '',
    referral: '',
    consentContact: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const interestAreas = [
    'Investing',
    'Governance',
    'Longevity',
    'Lifestyle',
    'Membership',
  ];

  const applyForOptions = [
    'Closed Investment Circle',
    'VIP Masterclass',
    'Private Salon',
  ];

  const handleCheckboxChange = (area: string, field: 'primaryInterest' | 'applyFor') => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(area)
        ? prev[field].filter((a) => a !== area)
        : [...prev[field], area],
    }));
  };

  const handleStartApplication = () => {
    setCurrentStep('form');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('title', formData.title);
      formBody.append('organization', formData.organization);
      formBody.append('primaryGeographies', formData.primaryGeographies);
      formBody.append('primaryInterest', formData.primaryInterest.join(', '));
      formBody.append('topGoals', formData.topGoals);
      formBody.append('applyFor', formData.applyFor.join(', '));
      formBody.append('dietaryNotes', formData.dietaryNotes);
      formBody.append('referral', formData.referral);
      formBody.append('consentContact', formData.consentContact);

      const response = await fetch('https://readdy.ai/api/form/d67ln2vmvg9ih2c79810', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setCurrentStep('review');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProceedToPayment = () => {
    setCurrentStep('confirmed');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isFormValid =
    formData.fullName &&
    formData.title &&
    formData.organization &&
    formData.primaryGeographies &&
    formData.primaryInterest.length > 0 &&
    formData.topGoals &&
    formData.referral &&
    formData.consentContact;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Request Invitation',
    description:
      'Apply to attend the Women In Wealth Summit in Beirut. Participation is exclusively by application or nomination.',
    about: {
      '@type': 'Event',
      name: 'Women In Wealth Summit',
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InvitationOnly',
        url: `${siteUrl}/attend`,
      },
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Request Invitation | Women In Wealth Summit · Beirut 2026"
        description="Apply to attend the Women In Wealth Summit in Beirut, March 2026. Participation is exclusively by application or nomination. A curated room designed for women who move capital and shape legacies."
        keywords="attend summit, request invitation, apply to attend, summit application, women in wealth application, invitation only summit"
        canonical={`${siteUrl}/attend`}
        schema={schema}
      />
      <Navigation />

      {/* Step 1: The Invitation Page */}
      {currentStep === 'invitation' && (
        <>
          <section className="min-h-screen flex items-center justify-center relative overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0">
              <img
                src="https://public.readdy.ai/ai/img_res/6a082e0ecee90b7bf8e085a13dee64d5.jpg"
                alt="Elegant venue"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-navy-950/80 via-navy-950/70 to-navy-950/90"></div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute top-1/4 left-0 w-72 h-72 bg-champagne-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-blush-500/10 rounded-full blur-3xl"></div>

            <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 text-center py-32">
              {/* Badge */}
              <div className="inline-block mb-10">
                <div className="px-6 py-2 bg-white/10 border border-champagne-400/30 backdrop-blur-sm rounded-full">
                  <span className="text-champagne-400 text-sm font-sans font-medium tracking-[0.2em] uppercase">
                    By Invitation Only
                  </span>
                </div>
              </div>

              {/* Main Headline */}
              <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-[1.05]">
                A room designed,<br />
                <span className="text-champagne-400 italic">not filled.</span>
              </h1>

              {/* Divider */}
              <div className="w-24 h-0.5 bg-champagne-400 mx-auto mb-10"></div>

              {/* Subheadline */}
              <p className="text-xl md:text-2xl text-white/80 font-sans leading-relaxed max-w-2xl mx-auto mb-14">
                Request an invitation to join{' '}
                <strong className="text-white">100 global women</strong> shaping the future of wealth.
              </p>

              {/* CTA Button */}
              <button
                onClick={handleStartApplication}
                className="group inline-flex items-center gap-4 px-12 py-6 bg-champagne-500 text-navy-950 font-sans font-semibold text-lg hover:bg-champagne-400 transition-all duration-300 hover:shadow-2xl hover:shadow-champagne-500/30 whitespace-nowrap cursor-pointer"
              >
                Request an Invitation
                <i className="ri-arrow-right-line text-2xl group-hover:translate-x-2 transition-transform"></i>
              </button>

              {/* Bottom Note */}
              <p className="mt-16 text-sm text-white/50 font-sans">
                Beirut · March 2026 · 100 Participants
              </p>
            </div>

            {/* Scroll Indicator */}
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
              <i className="ri-arrow-down-line text-2xl text-white/40"></i>
            </div>
          </section>
        </>
      )}

      {/* Step 2: The Application Form */}
      {currentStep === 'form' && (
        <>
          {/* Form Header */}
          <section className="pt-32 pb-16 bg-gradient-to-b from-navy-950 to-navy-900 relative overflow-hidden">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-96 h-96 bg-champagne-500 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-72 h-72 bg-blush-500 rounded-full blur-3xl"></div>
            </div>
            <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-12 text-center">
              <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-8">
                <p className="text-xs uppercase tracking-widest text-champagne-400">Application</p>
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
                Share Your Story
              </h1>
              <p className="text-lg text-white/70 font-sans">
                A thoughtful introduction to understand your journey and vision
              </p>
            </div>
          </section>

          {/* Form Section */}
          <section className="py-20 bg-[#FDFCFA]">
            <div className="max-w-2xl mx-auto px-6 lg:px-12">
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                id="attendance_application"
                className="space-y-8"
              >
                {/* Full Name */}
                <div className="space-y-2">
                  <label className="block text-sm font-sans font-medium text-navy-800">
                    Full Name <span className="text-champagne-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    required
                    className="w-full border-2 border-navy-200 focus:border-champagne-500 outline-none px-5 py-4 text-navy-900 font-sans transition-all bg-white"
                    placeholder="Your full name"
                  />
                </div>

                {/* Title / Role */}
                <div className="space-y-2">
                  <label className="block text-sm font-sans font-medium text-navy-800">
                    Title / Role <span className="text-champagne-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full border-2 border-navy-200 focus:border-champagne-500 outline-none px-5 py-4 text-navy-900 font-sans transition-all bg-white"
                    placeholder="e.g., Managing Partner, CEO, Principal"
                  />
                </div>

                {/* Organization */}
                <div className="space-y-2">
                  <label className="block text-sm font-sans font-medium text-navy-800">
                    Organization / Family Office <span className="text-champagne-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="organization"
                    value={formData.organization}
                    onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                    required
                    className="w-full border-2 border-navy-200 focus:border-champagne-500 outline-none px-5 py-4 text-navy-900 font-sans transition-all bg-white"
                    placeholder="Your organization or family office name"
                  />
                </div>

                {/* Primary Geographies */}
                <div className="space-y-2">
                  <label className="block text-sm font-sans font-medium text-navy-800">
                    Primary Geographies <span className="text-champagne-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="primaryGeographies"
                    value={formData.primaryGeographies}
                    onChange={(e) => setFormData({ ...formData, primaryGeographies: e.target.value })}
                    required
                    className="w-full border-2 border-navy-200 focus:border-champagne-500 outline-none px-5 py-4 text-navy-900 font-sans transition-all bg-white"
                    placeholder="e.g., Dubai, London, Singapore"
                  />
                </div>

                {/* Primary Interest */}
                <div className="space-y-4">
                  <label className="block text-sm font-sans font-medium text-navy-800">
                    Primary Interest <span className="text-champagne-600">*</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {interestAreas.map((area) => (
                      <label
                        key={area}
                        className={`flex items-center gap-3 p-4 border-2 cursor-pointer transition-all ${
                          formData.primaryInterest.includes(area)
                            ? 'border-champagne-500 bg-champagne-50'
                            : 'border-navy-200 bg-white hover:border-navy-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          name="primaryInterest"
                          checked={formData.primaryInterest.includes(area)}
                          onChange={() => handleCheckboxChange(area, 'primaryInterest')}
                          className="w-5 h-5 rounded border-2 border-navy-300 text-champagne-500 focus:ring-champagne-500 cursor-pointer"
                        />
                        <span className="text-sm text-navy-800 font-sans">{area}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Top 3 Goals */}
                <div className="space-y-2">
                  <label className="block text-sm font-sans font-medium text-navy-800">
                    Top 3 Goals for Attending <span className="text-champagne-600">*</span>
                  </label>
                  <textarea
                    name="topGoals"
                    value={formData.topGoals}
                    onChange={(e) => setFormData({ ...formData, topGoals: e.target.value })}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full border-2 border-navy-200 focus:border-champagne-500 outline-none px-5 py-4 text-navy-900 font-sans transition-all bg-white resize-none"
                    placeholder="Share your three primary objectives for joining the summit..."
                  ></textarea>
                  <p className="text-xs text-navy-400 text-right">{formData.topGoals.length}/500</p>
                </div>

                {/* Apply For */}
                <div className="space-y-4">
                  <label className="block text-sm font-sans font-medium text-navy-800">
                    Would you like to apply for
                  </label>
                  <div className="space-y-3">
                    {applyForOptions.map((option) => (
                      <label
                        key={option}
                        className={`flex items-center gap-3 p-4 border-2 cursor-pointer transition-all ${
                          formData.applyFor.includes(option)
                            ? 'border-champagne-500 bg-champagne-50'
                            : 'border-navy-200 bg-white hover:border-navy-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          name="applyFor"
                          checked={formData.applyFor.includes(option)}
                          onChange={() => handleCheckboxChange(option, 'applyFor')}
                          className="w-5 h-5 rounded border-2 border-navy-300 text-champagne-500 focus:ring-champagne-500 cursor-pointer"
                        />
                        <span className="text-sm text-navy-800 font-sans">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Dietary / Medical Notes */}
                <div className="space-y-2">
                  <label className="block text-sm font-sans font-medium text-navy-800">
                    Dietary / Medical Notes
                  </label>
                  <textarea
                    name="dietaryNotes"
                    value={formData.dietaryNotes}
                    onChange={(e) => setFormData({ ...formData, dietaryNotes: e.target.value })}
                    maxLength={500}
                    rows={3}
                    className="w-full border-2 border-navy-200 focus:border-champagne-500 outline-none px-5 py-4 text-navy-900 font-sans transition-all bg-white resize-none"
                    placeholder="Any dietary restrictions or medical considerations we should be aware of..."
                  ></textarea>
                  <p className="text-xs text-navy-400 text-right">{formData.dietaryNotes.length}/500</p>
                </div>

                {/* Referral */}
                <div className="space-y-2">
                  <label className="block text-sm font-sans font-medium text-navy-800">
                    Referral / How You Heard About the Summit <span className="text-champagne-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="referral"
                    value={formData.referral}
                    onChange={(e) => setFormData({ ...formData, referral: e.target.value })}
                    required
                    className="w-full border-2 border-navy-200 focus:border-champagne-500 outline-none px-5 py-4 text-navy-900 font-sans transition-all bg-white"
                    placeholder="e.g., Referred by [Name], LinkedIn, Industry publication"
                  />
                </div>

                {/* Consent to Contact */}
                <div className="space-y-4">
                  <label className="block text-sm font-sans font-medium text-navy-800">
                    Consent to be Contacted <span className="text-champagne-600">*</span>
                  </label>
                  <div className="space-y-3">
                    <label
                      className={`flex items-center gap-3 p-4 border-2 cursor-pointer transition-all ${
                        formData.consentContact === 'yes'
                          ? 'border-champagne-500 bg-champagne-50'
                          : 'border-navy-200 bg-white hover:border-navy-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="consentContact"
                        value="yes"
                        checked={formData.consentContact === 'yes'}
                        onChange={(e) => setFormData({ ...formData, consentContact: e.target.value })}
                        required
                        className="w-5 h-5 border-2 border-navy-300 text-champagne-500 focus:ring-champagne-500 cursor-pointer"
                      />
                      <span className="text-sm text-navy-800 font-sans">
                        Yes, I consent to be contacted for follow-on advisory or membership opportunities
                      </span>
                    </label>
                    <label
                      className={`flex items-center gap-3 p-4 border-2 cursor-pointer transition-all ${
                        formData.consentContact === 'no'
                          ? 'border-champagne-500 bg-champagne-50'
                          : 'border-navy-200 bg-white hover:border-navy-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="consentContact"
                        value="no"
                        checked={formData.consentContact === 'no'}
                        onChange={(e) => setFormData({ ...formData, consentContact: e.target.value })}
                        required
                        className="w-5 h-5 border-2 border-navy-300 text-champagne-500 focus:ring-champagne-500 cursor-pointer"
                      />
                      <span className="text-sm text-navy-800 font-sans">
                        No, please only contact me regarding this summit
                      </span>
                    </label>
                  </div>
                </div>

                {/* Note */}
                <div className="py-6 border-t border-navy-200">
                  <p className="text-sm text-navy-500 font-sans italic text-center">
                    Participation details are shared upon acceptance.
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!isFormValid || isSubmitting}
                  className="w-full py-5 bg-navy-900 text-white font-sans font-semibold text-lg hover:bg-navy-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer group"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-3">
                      <i className="ri-loader-4-line animate-spin text-xl"></i>
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-3">
                      Submit Application
                      <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
                    </span>
                  )}
                </button>

                {/* Back Link */}
                <button
                  type="button"
                  onClick={() => setCurrentStep('invitation')}
                  className="w-full py-4 text-navy-600 font-sans font-medium hover:text-navy-900 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back
                </button>
              </form>
            </div>
          </section>
        </>
      )}

      {/* Step 3: Thank You / Under Review Page */}
      {currentStep === 'review' && (
        <section className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-b from-navy-950 via-navy-900 to-navy-950">
          {/* Decorative Elements */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-champagne-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-blush-500 rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-12 text-center py-32">
            {/* Success Icon */}
            <div className="w-24 h-24 flex items-center justify-center bg-champagne-500/20 rounded-full mx-auto mb-10">
              <i className="ri-check-line text-5xl text-champagne-400"></i>
            </div>

            {/* Headline */}
            <h1 className="font-display text-4xl md:text-6xl font-bold text-white mb-8">
              Your application has been received.
            </h1>

            {/* Divider */}
            <div className="w-20 h-0.5 bg-champagne-400 mx-auto mb-10"></div>

            {/* Copy */}
            <div className="space-y-6 max-w-xl mx-auto">
              <p className="text-xl text-white/80 font-sans leading-relaxed">
                Our curation team reviews each profile with care to ensure alignment in values,
                ambition, and discretion.
              </p>
              <p className="text-lg text-champagne-400 font-sans font-medium">
                You will receive a private update within 7–10 days.
              </p>
            </div>

            {/* Demo: View Acceptance (for demonstration purposes) */}
            <div className="mt-16 pt-10 border-t border-white/10">
              <p className="text-sm text-white/40 font-sans mb-6">Women In Wealth · Beirut 2026</p>
              <button
                onClick={() => {
                  setCurrentStep('accepted');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="text-sm text-champagne-400/60 hover:text-champagne-400 transition-colors cursor-pointer underline underline-offset-4"
              >
                Preview Acceptance Page →
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Step 4: Acceptance Page */}
      {currentStep === 'accepted' && (
        <section className="min-h-screen relative overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20luxury%20interior%20with%20soft%20golden%20light%20streaming%20through%20sheer%20curtains%20sophisticated%20minimalist%20space%20with%20warm%20champagne%20and%20ivory%20tones%20exclusive%20private%20club%20atmosphere%20refined%20architectural%20details%20subtle%20shadows&width=1920&height=1080&seq=accept-bg-001&orientation=landscape"
              alt="Welcome"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-navy-950/90 via-navy-950/80 to-navy-950/95"></div>
          </div>

          {/* Decorative Elements */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
            <div className="absolute top-1/4 right-0 w-96 h-96 bg-champagne-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-blush-500/10 rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 lg:px-12 py-32">
            <div className="max-w-3xl mx-auto text-center">
              {/* Elegant Badge */}
              <div className="inline-block mb-12">
                <div className="relative">
                  <div className="absolute inset-0 bg-champagne-500/20 blur-xl rounded-full"></div>
                  <div className="relative px-8 py-3 bg-gradient-to-r from-champagne-500/20 via-champagne-400/30 to-champagne-500/20 border border-champagne-400/40 backdrop-blur-sm rounded-full">
                    <span className="text-champagne-300 text-sm font-sans font-medium tracking-[0.3em] uppercase">
                      You Have Been Selected
                    </span>
                  </div>
                </div>
              </div>

              {/* Main Headline */}
              <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-[1.05]">
                Welcome to<br />
                <span className="text-champagne-400 italic">the room.</span>
              </h1>

              {/* Elegant Divider */}
              <div className="flex items-center justify-center gap-4 mb-10">
                <div className="w-16 h-px bg-gradient-to-r from-transparent to-champagne-400/60"></div>
                <div className="w-2 h-2 rounded-full bg-champagne-400"></div>
                <div className="w-16 h-px bg-gradient-to-l from-transparent to-champagne-400/60"></div>
              </div>

              {/* Copy */}
              <div className="space-y-6 max-w-2xl mx-auto mb-14">
                <p className="text-xl md:text-2xl text-white/80 font-sans leading-relaxed">
                  You have been selected to join<br />
                  <strong className="text-white">Women In Wealth — Beirut 2026</strong>
                </p>
                <p className="text-lg text-white/60 font-sans leading-relaxed">
                  Our concierge will now guide you through the next steps, including accommodation, logistics, and private pre-summit circles.
                </p>
              </div>

              {/* Concierge Card */}
              <div className="max-w-md mx-auto mb-12">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-champagne-500/20 to-blush-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                  <div className="relative bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8">
                    <div className="flex items-center gap-5 mb-6">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-champagne-400 to-champagne-600 flex items-center justify-center shadow-lg">
                        <i className="ri-user-star-line text-2xl text-white"></i>
                      </div>
                      <div className="text-left">
                        <p className="text-xs uppercase tracking-widest text-champagne-400 font-semibold mb-1">Your Concierge</p>
                        <p className="text-white font-display font-bold text-lg">Summit Concierge Team</p>
                      </div>
                    </div>
                    <div className="space-y-3 text-left mb-6">
                      <div className="flex items-center gap-3 text-white/70">
                        <i className="ri-mail-line text-champagne-400"></i>
                        <span className="text-sm font-sans">concierge@womeninwealth.com</span>
                      </div>
                      <div className="flex items-center gap-3 text-white/70">
                        <i className="ri-whatsapp-line text-champagne-400"></i>
                        <span className="text-sm font-sans">+961 XX XXX XXX</span>
                      </div>
                    </div>
                    <p className="text-xs text-white/50 font-sans text-left">
                      Available Monday–Friday, 9AM–6PM (Beirut Time)
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={handleProceedToPayment}
                className="group inline-flex items-center gap-4 px-12 py-6 bg-champagne-500 text-navy-950 font-sans font-semibold text-lg hover:bg-champagne-400 transition-all duration-300 hover:shadow-2xl hover:shadow-champagne-500/30 whitespace-nowrap cursor-pointer"
              >
                Confirm Your Place
                <i className="ri-arrow-right-line text-2xl group-hover:translate-x-2 transition-transform"></i>
              </button>

              {/* Secondary Action */}
              <p className="mt-8 text-sm text-white/40 font-sans">
                Questions? <a href="mailto:concierge@womeninwealth.com" className="text-champagne-400/80 hover:text-champagne-400 transition-colors cursor-pointer underline underline-offset-4">Reach out to your concierge</a>
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Step 5: Payment / Confirmation Page */}
      {currentStep === 'confirmed' && (
        <section className="min-h-screen relative overflow-hidden bg-[#FDFCFA]">
          {/* Subtle Background Pattern */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-champagne-200 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blush-100 rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10 min-h-screen flex flex-col">
            {/* Header */}
            <div className="pt-32 pb-16 text-center px-6">
              <div className="inline-block mb-8">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-champagne-400 to-champagne-600 flex items-center justify-center mx-auto shadow-xl shadow-champagne-500/30">
                  <i className="ri-check-double-line text-4xl text-white"></i>
                </div>
              </div>
              <h1 className="font-display text-4xl md:text-6xl font-bold text-navy-900 mb-6">
                Your place is confirmed.
              </h1>
              <div className="w-20 h-0.5 bg-champagne-500 mx-auto mb-8"></div>
              <p className="text-xl text-navy-600 font-sans max-w-xl mx-auto leading-relaxed">
                You are now part of a global circle of women shaping capital, culture, and legacy.
              </p>
            </div>

            {/* Confirmation Details */}
            <div className="flex-1 flex items-start justify-center px-6 pb-32">
              <div className="w-full max-w-2xl">
                {/* Confirmation Card */}
                <div className="bg-white rounded-3xl shadow-xl shadow-navy-900/5 border border-navy-100 overflow-hidden">
                  {/* Card Header */}
                  <div className="bg-gradient-to-r from-navy-900 to-navy-800 px-8 py-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-widest text-champagne-400 font-semibold mb-1">Confirmation</p>
                        <p className="text-white font-display font-bold text-xl">Women In Wealth Summit</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-white/60 font-sans">Beirut</p>
                        <p className="text-champagne-400 font-sans font-semibold">March 2026</p>
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-8 space-y-8">
                    {/* Event Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-1">
                        <p className="text-xs uppercase tracking-wider text-navy-400 font-semibold">Duration</p>
                        <p className="text-navy-900 font-sans font-medium">2 Days</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs uppercase tracking-wider text-navy-400 font-semibold">Location</p>
                        <p className="text-navy-900 font-sans font-medium">Beirut, Lebanon</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs uppercase tracking-wider text-navy-400 font-semibold">Participants</p>
                        <p className="text-navy-900 font-sans font-medium">100 Global Women</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs uppercase tracking-wider text-navy-400 font-semibold">Status</p>
                        <p className="text-emerald-600 font-sans font-semibold flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                          Confirmed
                        </p>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-navy-100"></div>

                    {/* What's Included */}
                    <div>
                      <p className="text-xs uppercase tracking-wider text-navy-400 font-semibold mb-4">Your Experience Includes</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[
                          'Full Summit Access',
                          'Gala Dinner',
                          'Private Networking Sessions',
                          'Curated Experiences',
                          'Concierge Support',
                          'Pre-Summit Circles',
                        ].map((item, index) => (
                          <div key={index} className="flex items-center gap-3">
                            <div className="w-5 h-5 rounded-full bg-champagne-100 flex items-center justify-center">
                              <i className="ri-check-line text-xs text-champagne-600"></i>
                            </div>
                            <span className="text-sm text-navy-700 font-sans">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-navy-100"></div>

                    {/* Next Steps */}
                    <div className="bg-gradient-to-br from-champagne-50 to-blush-50 rounded-2xl p-6">
                      <p className="text-xs uppercase tracking-wider text-champagne-700 font-semibold mb-4">Next Steps</p>
                      <div className="space-y-4">
                        <div className="flex items-start gap-4">
                          <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-champagne-600">1</span>
                          </div>
                          <div>
                            <p className="text-navy-900 font-sans font-medium text-sm">Check Your Email</p>
                            <p className="text-navy-500 font-sans text-xs">Detailed itinerary and welcome materials</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-4">
                          <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-champagne-600">2</span>
                          </div>
                          <div>
                            <p className="text-navy-900 font-sans font-medium text-sm">Connect with Concierge</p>
                            <p className="text-navy-500 font-sans text-xs">Arrange accommodation and travel logistics</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-4">
                          <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-champagne-600">3</span>
                          </div>
                          <div>
                            <p className="text-navy-900 font-sans font-medium text-sm">Join Pre-Summit Circle</p>
                            <p className="text-navy-500 font-sans text-xs">Exclusive virtual gathering before Beirut</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="bg-navy-50 px-8 py-6">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <i className="ri-customer-service-2-line text-xl text-champagne-600"></i>
                        <span className="text-sm text-navy-600 font-sans">Need assistance? Contact your concierge</span>
                      </div>
                      <a
                        href="mailto:concierge@womeninwealth.com"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-navy-900 text-white rounded-full font-sans font-medium text-sm hover:bg-navy-800 transition-all cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-mail-line"></i>
                        Email Concierge
                      </a>
                    </div>
                  </div>
                </div>

                {/* Bottom Note */}
                <div className="text-center mt-10">
                  <p className="text-sm text-navy-400 font-sans">
                    We look forward to welcoming you in Beirut.
                  </p>
                  <p className="text-xs text-navy-300 font-sans mt-2">
                    Women In Wealth · March 2026
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {currentStep !== 'review' && currentStep !== 'accepted' && currentStep !== 'confirmed' && <Footer />}
    </div>
  );
}